import { loadBasicColumnChart } from 'app/Requests';
import { _t } from 'app/utils/i18n';
import React, { FunctionComponent, memo, useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';

import styles from './CustomColumnChart.module.scss';
import CardTitle from 'app/components/CardTitle';
import {
  PlatformIcon,
  CountryIcon,
  ExpandIcon,
  HashtagsIcon,
} from 'app/assets/images/icons';
import SelectInput from 'app/components/Form/Inputs/SelectInput';
import DotsMenu from 'app/components/DotsMenu';

import TimeIntervalFilter from 'app/components/TimeIntervalFilter';
import { nFormatter } from 'app/utils/numbersFormater';
import { useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import ReactSelectAutocomplete from 'app/components/Form/Inputs/ReactSelectAutocomplete';
import { fixed24HoursFormatter } from 'app/utils/dateTimeFormatter';
import { handelNameKey } from 'app/utils/handel-name-key';
import { checkTranslated } from 'app/utils/helpers';
import { getFromLocalStorage } from 'app/utils/localStorage';
import { Grid } from '@material-ui/core';
import ServerError from 'app/components/ErrorBoundary/ServerError';
import { useAppSettings } from 'app/context/settings/appSettings';
import { flatMap } from 'app/utils/array-flat-map';

const lang = getFromLocalStorage('LNG');

type ChartData = {
  values: Array<{ name: string; data: Array<any> }>;
  xaxis: Array<string>;
  tooltip: any;
};
interface Props {
  data?: any;
  slug?: string;
  timeIntervalFilter?: boolean;
  platformFilter?: boolean;
  customFilters?: any;
  columnHeader?: string[];
  showToolTip?: boolean;
  usersTooltip?: boolean;
  mentionsTooltip?: boolean;
  clientSourceTooltip?: boolean;
  searchCountTooltip?: boolean;
  newsCountTooltip?: boolean;
  basicColumnTitle?: string;
  compId: string;
  showCustomizedLegend?: boolean;
  removeLegends?: boolean;
  usersCountToolip?: boolean;
  countryFilter?: boolean;
  distributed?: boolean;
  singleColumnChart?: boolean;
  showAllTimeInterval?: boolean;
  categoryNamesTooltip?: boolean;
  weekTimeInterval?: boolean;
  monthTimeInterval?: boolean;
  hashtagFilter?: string;
  expandUrl?: string;
  sliceArray?: boolean;
  changebarColor?: boolean;
  dayLegend?: boolean;
  trendingTooltip?: boolean;
  withTranslatedData?: boolean;
  monthTimeIntervalWithoutDay?: boolean;
  translateToolTip?: boolean;
  tooltipWithSlug?: boolean;
  grid?: any;
  hideIfNoData?: boolean;
  numOfNewsGrabbedTooltip?: boolean;
  additionalComponents?: JSX.Element;
  isReport?: boolean;
  withLegendCount?: boolean;
  testLoading?: boolean;
}

const CustomColumnChart: FunctionComponent<Props> = ({
  data,
  compId,
  basicColumnTitle,
  slug,
  timeIntervalFilter,
  platformFilter,
  customFilters,
  columnHeader,
  usersTooltip,
  showCustomizedLegend,
  mentionsTooltip: mentionsTooltip,
  clientSourceTooltip: clientSourceTooltip,
  searchCountTooltip,
  newsCountTooltip,
  showToolTip,
  usersCountToolip,
  countryFilter,
  removeLegends,
  distributed,
  singleColumnChart,
  showAllTimeInterval,
  categoryNamesTooltip,
  weekTimeInterval,
  monthTimeInterval,
  hashtagFilter,
  expandUrl,
  sliceArray,
  changebarColor,
  dayLegend,
  trendingTooltip,
  withTranslatedData,
  monthTimeIntervalWithoutDay,
  translateToolTip,
  tooltipWithSlug,
  grid,
  hideIfNoData,
  numOfNewsGrabbedTooltip,
  additionalComponents,
  isReport,
  withLegendCount,
  testLoading,
}) => {
  const queryClient = useQueryClient();
  const { theme } = useAppSettings();
  const countriesOptions: any = queryClient.getQueryData('getCountries');
  const platforms = queryClient.getQueryData('getPlatforms') as any;
  const defaultCountriesIds = countriesOptions
    ?.filter((option: { default: any }) => option?.default)
    ?.map((i: { value: any }) => i.value);

  useEffect(() => {
    setFilters({
      ...customFilters,
      country_ids: countryFilter ? defaultCountriesIds : null,
    });
    return () => {
      // queryClient.removeQueries([
      //   'loadBasicColumnChart',
      //   { slug, body: filters },
      // ]);
    };
  }, [customFilters]);

  const [filters, setFilters] = useState<any>({
    ...customFilters,
    country_ids: countryFilter ? defaultCountriesIds : null,
  });

  const handleChartDataSchema = (data: any) =>
    data?.map((item: any) => item?.data?.[0]);

  const {
    data: QData,
    isLoading: laodCustomColumn,
    isError,
  } = useQuery(
    ['loadBasicColumnChart', { slug, body: filters }],
    loadBasicColumnChart,
    {
      enabled: !!slug,
      staleTime: 0,
      cacheTime: 0,
    },
  );
  const isLoading = testLoading ? testLoading : laodCustomColumn;
  const convertData = (data: any) => {
    if (singleColumnChart) {
      const newDataFormat = handleChartDataSchema(data?.values);
      return {
        ...data,
        values: [{ data: newDataFormat }],
      };
    } else {
      return data;
    }
  };
  let newData = QData;
  const translatedChartData = (data: any) => {
    const newData = {
      ...data,
      values: data?.values?.map((val: any) => ({
        name: checkTranslated(val?.name?.original, val?.name?.arabic),
        data: val?.data,
      })),
    };

    return newData;
  };
  if (translateToolTip) {
    newData = {
      ...newData,
      tooltip: newData?.tooltip?.map((item: any) => ({
        ...item,
        modules: item.modules?.map((module: { module_name: string }) => ({
          ...module,
          module_name: _t(`basic:${module?.module_name?.toLowerCase()}`),
        })),
      })),
    };
  }
  const chartData = sliceArray
    ? {
        ...newData,
        values: newData?.values?.slice(0, 1),
        xaxis: newData?.xaxis?.map((_: any, index: number) =>
          fixed24HoursFormatter(index, false, filters.time_interval),
        ),
      }
    : !!slug
      ? convertData(newData)
      : !!data
        ? data
        : { values: [], xaxis: [], tooltip: [] };
  const newChartData = withTranslatedData
    ? translatedChartData(chartData)
    : chartData;
  const handleChangeFilters = (name: string) => (option: any) => {
    if (Array.isArray(option)) {
      const values = option.map((item: any) => Number(item.value));
      setFilters((prevFilters: any) => ({ ...prevFilters, [name]: values }));
    } else {
      setFilters((prevFilters: any) => ({
        ...prevFilters,
        [name]: option.value,
      }));
    }
  };
  const dataFromValues = newChartData?.values?.map(
    (val: { data: any }) => val?.data,
  );
  const checkEveryDataIsFalsy = flatMap(dataFromValues)?.every(
    (item) => item === 0,
  );
  const handleChangeTime = (option: any) => {
    setFilters({
      ...filters,
      time_interval: option,
    });
  };
  const customToolTip = (
    x: any,
    tooltip: any,
    columnHeader: any,
    usersTooltip: boolean,
    mentionsTooltip: boolean,
    clientSourceTooltip: boolean,
    searchCountTooltip: boolean,
    newsCountTooltip: boolean,
    series: any,
    seriesIndex: any,
    w: any,
    usersCountToolip: boolean,
    categoryNamesTooltip: boolean,
    trendingTooltip: boolean,
    tooltipWithSlug: boolean,
    numOfNewsGrabbedTooltip: any,
  ) => {
    if (mentionsTooltip) {
      return `<p class="mentions_tooltip" data-testid='mentions_tooltip_testid'>${nFormatter(
        series[seriesIndex][x],
      )} ${_t('trends:Mentions')} ${_t('basic:in')} ${w.globals.labels[x]}</p>`;
    } else if (clientSourceTooltip) {
      return `<p class="mentions_tooltip">
      ${w.globals.labels[x]} <br/> ${nFormatter(series[seriesIndex][x])}</p>`;
    } else if (usersTooltip) {
      return `<div class="user_tooltip">
      <p class="title">${columnHeader[0]}</p>
      ${tooltip[x].users
        ?.map((user: any) => {
          return `<div class="user_info">
          ${
            user.image
              ? `<span class="user_image defaultImg">
                      <img src="${user.image}"/>
                   </span>`
              : `<span class="user_image defaultImg">
              <svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 36 36">
              <g id="Group_7689" data-name="Group 7689" transform="translate(0.779 -0.023)">
                <circle id="Ellipse_14" data-name="Ellipse 14" cx="17.5" cy="17.5" r="17.5" transform="translate(-0.279 0.523)" fill="rgba(var(--secondaryColorRGB),0.13)" stroke="var(--secondaryColor)" stroke-miterlimit="10" stroke-width="1"/>
                <g id="Group_140" data-name="Group 140" transform="translate(12.015 11.065)">
                  <path id="Path_50" data-name="Path 50" d="M19.167,29.5H18.5c-2.053,0-5,1.383-5,3.7a1.089,1.089,0,0,0,1.026,1.2h8.657a1.074,1.074,0,0,0,1.026-1.16V33.2C24.21,30.883,21.265,29.5,19.167,29.5Z" transform="translate(-13.5 -21.021)" fill="#fff" stroke="var(--secondaryColor)" stroke-miterlimit="10" stroke-width="1"/>
                  <path id="Path_51" data-name="Path 51" d="M21.624,18.086a2.526,2.526,0,0,0,1.919-.8,5.679,5.679,0,0,0,1.2-3.659,3.124,3.124,0,1,0-6.248,0,5.842,5.842,0,0,0,1.2,3.7A2.66,2.66,0,0,0,21.624,18.086Z" transform="translate(-16.268 -10.5)" fill="#fff" stroke="var(--secondaryColor)" stroke-miterlimit="10" stroke-width="1"/>
                </g>
              </g>
            </svg>
                   </span>`
          }
            <p>${user.username}</p>
          </div>`;
        })
        .join('')}
      </div>`;
    } else if (usersCountToolip) {
      return `<p class="mentions_tooltip">${series[seriesIndex][x]} ${_t(
        'users:user',
      )}`;
    } else if (searchCountTooltip) {
      return `
        <div class="searchCountTooltip">
          <h3>${_t('topics:search_count')}</h3>
          <p>${nFormatter(series[seriesIndex][x])}</p>
        </div>`;
    } else if (newsCountTooltip) {
      return `<div class="user_tooltip">
      <h3 class="mb-10">${_t('news:source_editions')} (${
        tooltip[x]?.length
      })</h3>
      ${tooltip[x]
        ?.map((source: any) => {
          return `<div class="user_info">
          ${
            source?.image
              ? `<span class="user_image defaultImg">
                      <img src="${source?.image}"/>
                   </span>`
              : `<span class="user_image defaultImg">
                     <span>  <svg xmlns="http://www.w3.org/2000/svg" width="103" height="103" viewBox="0 0 103 103">
                     <g id="Group_14590" data-name="Group 14590" transform="translate(0 0)">
                       <circle id="Ellipse_14" data-name="Ellipse 14" cx="51" cy="51" r="51" transform="translate(0.5 0.5)" fill="rgba(var(--secondaryColorRGB),0.2)" stroke="var(--secondaryColor)" stroke-miterlimit="10" stroke-width="1"/>
                       <g id="Component_10_8" data-name="Component 10 – 8" transform="translate(34 40)">
                         <path id="Path_11330" data-name="Path 11330" d="M35,16.5l-5.657-6.01-6.481,6.835L10.488,3.653,0,15.2v7.189H35ZM24.394,0a3.12,3.12,0,0,1,3.064,3.064,3.12,3.12,0,0,1-3.064,3.064A3.043,3.043,0,0,1,21.33,3.064,3.043,3.043,0,0,1,24.394,0Z" fill="#fff" stroke="var(--secondaryColor)" stroke-width="1" fill-rule="evenodd"/>
                       </g>
                     </g>
                   </svg>
                    </span> 
                   </span>`
          }
            <p>${source?.name}</p>
          </div>`;
        })
        ?.splice(0, 5)
        ?.join('')}
      </div>`;
    } else if (categoryNamesTooltip) {
      return `
        <div class="categoryNamesTooltip">
          <h3 dir="auto">${
            w?.globals?.labels[x]?.length > 20
              ? w?.globals?.labels[x]?.slice(0, 20) + '...'
              : w?.globals?.labels[x]
          }</h3>
          <div>
            ${tooltip[w?.globals?.labels[x]].slice(0, 5).map((el: any) => {
              return `<p dir="auto">${
                handelNameKey(el?.name)?.length < 20
                  ? handelNameKey(el?.name)
                  : handelNameKey(el?.name)?.slice(0, 20) + '...'
              }</p>`;
            })}
          </div>
        </div>`;
    } else if (trendingTooltip) {
      const names = tooltipWithSlug
        ? w?.globals?.initialSeries?.[0]?.tooltip?.[x]
            .map((el: any) => {
              return `<div dir='auto' style={{display:'flex' justifyContent:'center'}}>${handelNameKey(el?.name)}</div>`;
            })
            .join(' ')
        : data?.values?.[0]?.tooltip?.[x]
            .map((el: any) => {
              return `<div>${handelNameKey(el?.name)}</div>`;
            })
            .join(' ');
      const mentions = tooltipWithSlug
        ? w?.globals?.initialSeries?.[0]?.tooltip?.[x]
            ?.map((item: any) => {
              return `<div>${nFormatter(item?.mentions)}</div>`;
            })
            .join(' ')
        : data?.values?.[0]?.tooltip?.[x]
            .map((item: any) => {
              return `<div>${nFormatter(item?.mentions)}</div>`;
            })
            .join(' ');
      return `<div class="trending_compare">
                <h6><svg xmlns="http://www.w3.org/2000/svg" width="10.007" height="10.007" viewBox="0 0 10.007 10.007"><g transform="translate(151.615 -10)"><g transform="translate(-151.615 10)"><path d="M-142.273,12.517a4.989,4.989,0,0,0-.8-1.052A4.973,4.973,0,0,0-146.611,10a5.008,5.008,0,0,0-2.868.9,5.006,5.006,0,0,0-2.136,4.1,5.006,5.006,0,0,0,2.136,4.1,5.008,5.008,0,0,0,2.868.9,5,5,0,0,0,4.338-2.512A4.967,4.967,0,0,0-141.608,15,4.948,4.948,0,0,0-142.273,12.517Z" transform="translate(151.615 -10)" fill="var(--mainColor)"/></g><path d="M-141.915,13.1v3.4h3.4" transform="translate(-4.696 -1.501)" fill="none" stroke="#fff" stroke-width="1"/></g></svg>${
                  w?.globals?.labels[x]
                }</h6>
                <div class="arrow_box">
                  <div dir='auto' >
                    <div class="head_title">${_t('hashtags:hashtags')}</div>
                    <div class="subCategory">${names}</div>
                  </div>
                  <div>
                    <div class="head_title">${_t('hashtags:mentions')}</div>
                    <div>${mentions}</div>
                  </div>
                </div>
              </div>
         `;
    } else if (numOfNewsGrabbedTooltip) {
      const firstBarCount = w?.globals?.series[0]?.[x];
      const secondBarCount = w?.globals?.series[1]?.[x];
      const firstBarName = w?.globals?.seriesNames?.[0];
      const secondBarName = w?.globals?.seriesNames?.[1];
      const firstBarColor = '#FFCDCD';
      const secondBarColor = '#EBEDDA';

      return `<div class="trending_compare">
      <div class="arrow_box">
        <div>
          <div class="head_title" style="display: flex; align-items: center"><span style="background-color: ${firstBarColor}; width: 12px; height: 12px;display: inline-block;
          margin-inline-end: 5px;"></span>${firstBarName}</div>
          <div>${firstBarCount}</div>
        </div>
        <div>
          <div class="head_title" style="display: flex; align-items: center"><span style="background-color: ${secondBarColor}; width: 12px; height: 12px;display: inline-block;
          margin-inline-end: 5px;"></span>${secondBarName}</div>
          <div>${secondBarCount}</div>
        </div>
      </div>
    </div>
`;
    } else {
      return `<div class="arrow_box">
               <div>
                 <h6>${columnHeader[0]}</h6>
                ${tooltip
                  .map((el: any) => {
                    if (el.item === w.globals.labels[x]) {
                      return el.modules
                        ?.map(
                          (m: any) =>
                            `<p>${checkTranslated(m.name, m.name_ar)||m.module_name}<p>`,
                        )
                        .join(' ');
                    }
                  })
                  .join(' ')}
               </div>
               <div>
                 <h6>${columnHeader[1]}</h6>
                 ${tooltip
                   .map((el: any) => {
                     if (el.item === w.globals.labels[x]) {
                       return el.modules
                         ?.map((m: any) => `<p>${m.count}<p>`)
                         .join(' ');
                     }
                   })
                   .join(' ')}
               </div>
             </div>
       `;
    }
  };

  if (!newData && hideIfNoData && !isLoading) return <></>;
  return (
    <ServerError requiredApiCalls={[isError]}>
      <Grid item id={compId} {...grid}>
        <div id={`${compId}-basicColumn`} className="position-relative">
          {basicColumnTitle && (
            <CardTitle
              title={basicColumnTitle}
              compId={compId}
              hideIcon={isReport}
            />
          )}
          <div
            className={styles.CustomColumnChart}
            style={isReport ? { padding: 0, minHeight: 'unset' } : undefined}
            id={`${compId}-basicColumnChart`}
          >
            <div
              style={{
                display: 'flex',
                gap: 20,
                flexDirection: 'row-reverse',
              }}
            >
              <div className={styles.toolBar}>
                <div
                  className={styles.Filters}
                  id={`${compId}-basicColumnChart-platFormFilters`}
                  data-testid="platform-filter-holder"
                >
                  {countryFilter && (
                    <div className={styles.Select}>
                      <SelectInput
                        icon={<CountryIcon />}
                        placeholder={_t('trends:Country')}
                        options={countriesOptions as any}
                        isMulti
                        onChange={handleChangeFilters('country_ids')}
                        showDefaultValue
                        name={''}
                      />
                    </div>
                  )}
                  {hashtagFilter && (
                    <div
                      className={styles.Select}
                      data-testid="hashtag-filter-testid"
                    >
                      <ReactSelectAutocomplete
                        placeholder={_t('trends:hashtag')}
                        icon={<HashtagsIcon />}
                        isMulti={true}
                        moduleName={hashtagFilter}
                        onChange={handleChangeFilters('hashtag_ids')}
                      />
                    </div>
                  )}
                  {platformFilter && (
                    <div className={styles.Select}>
                      <SelectInput
                        icon={<PlatformIcon />}
                        placeholder={_t('trends:Platforms')}
                        options={platforms?.platformsOptions}
                        isMulti
                        onChange={handleChangeFilters('platform_ids')}
                        name={''}
                      />
                    </div>
                  )}
                  {timeIntervalFilter && (
                    <TimeIntervalFilter
                      compId={`${compId}-basicColumn`}
                      handleChangeTime={handleChangeTime}
                      hideDotsMenu
                      allTimeInterval={showAllTimeInterval}
                      weekTimeInterval={weekTimeInterval}
                      monthTimeInterval={monthTimeInterval}
                      monthTimeIntervalWithoutDay={monthTimeIntervalWithoutDay}
                      isReport={isReport}
                    />
                  )}
                </div>
                <div className={styles.Actions}>
                  {expandUrl && (
                    <Link
                      className={styles.ActionBtn}
                      to={{
                        pathname: expandUrl,
                      }}
                      data-testid="expandUrl-testid"
                    >
                      <ExpandIcon />
                    </Link>
                  )}
                  <div
                    className={styles.ActionBtn}
                    id={`${compId}-basicColumnChart-platFormFilters`}
                  >
                    {!isReport && <DotsMenu id={`${compId}-basicColumn`} />}
                  </div>
                </div>
              </div>
              {showCustomizedLegend && (
                <div
                  className={styles.specityChartHolder}
                  id={`${compId}-stackedColumn-legend`}
                  style={{
                    display: removeLegends ? 'none' : 'flex',
                    flexDirection: lang == 'ar' ? 'row-reverse' : 'row',
                  }}
                >
                  {dayLegend ? (
                    <div className={styles.specifyChart}>
                      <div
                        className={styles.colorBox}
                        style={{
                          borderColor: changebarColor
                            ? theme.colors.chartsColors[1]
                            : theme.colors.chartsColors[0],
                          backgroundColor: changebarColor
                            ? theme.colors.chartsColors[1]
                            : theme.colors.chartsColors[0],
                        }}
                      ></div>
                      <p>{handelNameKey(newChartData?.values?.[0].name)}</p>
                    </div>
                  ) : (
                    newChartData?.values?.[0]?.data?.map((val: any, i: any) => {
                      return (
                        <div key={i} className={`${styles.specifyChart} `}>
                          <div
                            className={styles.colorBox}
                            style={{
                              borderColor: theme.colors.chartsColors[i],
                              backgroundColor: theme.colors.chartsColors[i],
                            }}
                          ></div>
                          {withLegendCount ? (
                            <p>{`${newChartData?.xaxis?.[i]}(${newChartData?.values?.[0]?.data?.[i]})`}</p>
                          ) : (
                            <p>{`${newChartData?.xaxis?.[i]}`}</p>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
            {!isLoading && (
              <div data-testid="chart-holder">
                <ReactApexChart
                  options={
                    getDataLabelOptions(
                      theme.colors.chartsColors,
                      newChartData?.xaxis,
                      customToolTip,
                      newChartData?.tooltip,
                      columnHeader,
                      usersTooltip,
                      mentionsTooltip,
                      clientSourceTooltip,
                      searchCountTooltip,
                      newsCountTooltip,
                      showToolTip,
                      showCustomizedLegend,
                      usersCountToolip,
                      distributed,
                      categoryNamesTooltip,
                      changebarColor,
                      dayLegend,
                      trendingTooltip,
                      tooltipWithSlug,
                      numOfNewsGrabbedTooltip,
                      checkEveryDataIsFalsy,
                      withLegendCount,
                      filters,
                    ) as any
                  }
                  series={newChartData?.values}
                  type="bar"
                  height={200}
                />
              </div>
            )}
          </div>
          {additionalComponents}
        </div>
      </Grid>
    </ServerError>
  );
};
const MemoizedCustomColumnChart = memo(CustomColumnChart);
export default MemoizedCustomColumnChart;

function getDataLabelOptions(
  colors: string[],
  categories: string[],
  customTooltip?: any,
  tooltip?: any,
  columnHeader?: string[],
  usersTooltip?: boolean,
  mentionsTooltip?: boolean,
  clientSourceTooltip?: boolean,
  searchCountTooltip?: boolean,
  newsCountTooltip?: boolean,
  showToolTip?: boolean,
  showCustomizedLegend?: boolean,
  usersCountToolip?: boolean,
  distributed?: boolean,
  categoryNamesTooltip?: boolean,
  changebarColor?: boolean,
  dayLegend?: boolean,
  trendingTooltip?: boolean,
  tooltipWithSlug?: boolean,
  numOfNewsGrabbedTooltip?: boolean,
  checkEveryDataIsFalsy?: boolean,
  withLegendCount?: boolean,
  filters?: any,
) {
  const options = {
    chart: {
      type: 'bar',
      toolbar: {
        show: false,
      },
    },
    colors: changebarColor ? colors[1] : colors,

    xaxis: {
      type: 'category',
      categories: categories,
      labels: {
        formatter: function (value: string) {
          return value && value?.length > 15
            ? value.slice(0, 15) + '...'
            : value;
        },
        rotate: -45,
        rotateAlways: filters?.time_interval == 'YEAR',
      },
    },
    legend: {
      show: !showCustomizedLegend,
      // showForSingleSeries: true,
      onItemClick: {
        toggleDataSeries: false,
      },
      onItemHover: {
        highlightDataSeries: true,
      },
      formatter: function (seriesName: string, opts: any) {
        const values = opts?.w?.globals?.series?.[0]?.[opts?.seriesIndex];
        const series_Name =
          seriesName?.length > 30
            ? `${seriesName?.slice(0, 30)}.....`
            : seriesName;
        return withLegendCount
          ? `${series_Name} (${nFormatter(values)})`
          : series_Name;
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    yaxis: {
      show: true,
      showAlways: false,
      showForNullSeries: true,
      opposite: false,
      reversed: false,
      logarithmic: false,
      labels: {
        formatter: (value: any, index: number) =>
          checkEveryDataIsFalsy
            ? nFormatter(Math.ceil(index))
            : nFormatter(Math.ceil(value)),
      },
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      opacity: 1,
    },
    grid: {
      show: false,
    },
    labels: {
      style: {
        colors: colors,
      },
    },
    plotOptions: {
      bar: {
        columnWidth: '45%',
        distributed: distributed,
      },
    },
    tooltip: {
      enabled: showToolTip,
      custom: function ({ dataPointIndex, series, seriesIndex, w }: any) {
        return customTooltip(
          dataPointIndex,
          tooltip,
          columnHeader,
          usersTooltip,
          mentionsTooltip,
          clientSourceTooltip,
          searchCountTooltip,
          newsCountTooltip,
          series,
          seriesIndex,
          w,
          usersCountToolip,
          categoryNamesTooltip,
          trendingTooltip,
          tooltipWithSlug,
          numOfNewsGrabbedTooltip,
        );
      },
      shared: false,
      intersect: true,
    },
  };
  return options;
}
